import useIsEmea from "@dana/Common/Hooks/useIsEmea";
import CustomerAddressForm from "@dana/Overrides/Components/CustomerAddressForm";
import { Cart, danaValidateAddress } from "@dana/Overrides/Services/CartService";
import ShipToSelectorModal, {
    ShipToSelectorModalStyles,
} from "@dana/Overrides/Widgets/OnePageCheckout/ShipToSelectorModal";
import { getDanaGeneralSettings } from "@dana/Store/Context/ContextSelectors";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import { SoldToAddress } from "@dana/Types/ApiModels";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import { scrollToFirstFormError } from "@insite/client-framework/Common/CustomerSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import setAddressBookOverlayIsOpen from "@insite/client-framework/Store/Components/AddressBookOverlay/Handlers/SetAddressBookOverlayIsOpen";
import setStorePickupOverlayIsOpen from "@insite/client-framework/Store/Components/StorePickupOverlay/Handlers/SetStorePickupOverlayIsOpen";
import { getCurrentUserIsGuest, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import loadShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTos";
import { getShipTosDataView, getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import saveShipping from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SaveShipping";
import setIsBillingAddressTheSameAsShipping from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetIsBillingAddressTheSameAsShipping";
import setPaymentDetailsState from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetPaymentDetailsState";
import updateShipTo from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/UpdateShipTo";
import translate from "@insite/client-framework/Translate";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import { CustomerAddressFormStyles } from "@insite/content-library/Components/CustomerAddressForm";
import { OnePageCheckoutFormContext } from "@insite/content-library/Pages/OnePageCheckoutPage";
import BillingAddressForm, {
    BillingAddressFormStyles,
} from "@insite/content-library/Widgets/OnePageCheckout/BillingAddressForm";
import {
    BillToSelectorModalStyles,
} from "@insite/content-library/Widgets/OnePageCheckout/BillToSelectorModal";
import ShippingAddressForm, {
    ShippingAddressFormStyles,
} from "@insite/content-library/Widgets/OnePageCheckout/ShippingAddressForm";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { HasToasterContext } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const mapStateToProps = (state: ApplicationStateDana) => {
    const cartMode = getCookie("cartMode");
    const firstCustomerSelect = getCookie("selectBillLa");
    const { cartId } = state.pages.onePageCheckout;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const cart =
        cartMode === "StockOrderCart"
            ? cartState.value
            : cartMode === "EmergencyOrderCart"
                ? ((state.emergencyCartState.cart as Cart) ?? ({ properties: {} } as Cart))
                : ((state.annualReturnsCartState.cart as Cart) ?? ({ properties: {} } as Cart));

    const shipTosDataView = cart
        ? getShipTosDataView(state, { billToId: cart.billToId, expand: ["validation"], exclude: ["showAll"] })
        : undefined;
    let newAddress;
    if (cart && cart.cartLines && shipTosDataView && shipTosDataView.value) {
        const shipTos = shipTosDataView.value;
        newAddress = shipTos!.find(s => s.isNew && !s.oneTimeAddress);
    }
    const danaGeneralSettings = getDanaGeneralSettings(state);
    return {
        isShippingAddressUpdateRequired: state.pages.onePageCheckout.isShippingAddressUpdateRequired,
        isBillingAddressUpdateRequired: state.pages.onePageCheckout.isBillingAddressUpdateRequired,
        isBillingAddressTheSameAsShipping: state.pages.onePageCheckout.isBillingAddressTheSameAsShipping,
        isSavingShipping: state.pages.onePageCheckout.isSavingShipping,
        cart,
        shipToState: getShipToState(state, cart?.shipToId),
        billToState: getBillToState(state, cart?.billToId),
        currentUserIsGuest: getCurrentUserIsGuest(state),
        countries: getCurrentCountries(state),
        shipTosDataView: cart
            ? getShipTosDataView(state, { billToId: cart.billToId, expand: ["validation"], exclude: ["showAll"] })
            : undefined,
        shipToAddressFields: getAddressFieldsDataView(state).value?.shipToAddressFields,
        billToAddressFields: getAddressFieldsDataView(state).value?.billToAddressFields,
        fulfillmentMethod: getSession(state).fulfillmentMethod,
        pickUpWarehouse: getSession(state).pickUpWarehouse,
        newAddress,
        shipToText: cartMode === "AnnualReturnsOrderCart" ? "Ship-from" : "Ship-to",
        defaultCustomer: getShipToState(state, firstCustomerSelect ?? ""),
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        isEmea: useIsEmea(state),
        danaGeneralSettings
    };
};

const mapDispatchToProps = {
    loadShipTos,
    loadShipTo,
    loadBillTo,
    setIsBillingAddressTheSameAsShipping,
    setAddressBookOverlayIsOpen,
    setStorePickupOverlayIsOpen,
    saveShipping,
    setPaymentDetailsState,
    updateShipTo,
};

type Props = WidgetProps &
    HasHistory &
    HasToasterContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

export interface OnePageCheckoutShippingSectionStyles {
    loadingWrapper?: InjectableCss;
    container?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    titleEditGridItem?: GridItemProps;
    titleEditLink?: LinkPresentationProps;
    pickUpGridItem?: GridItemProps;
    headingGridItem?: GridItemProps;
    heading?: TypographyPresentationProps;
    errorMessage?: TypographyPresentationProps;
    editLink?: LinkPresentationProps;
    shipToSelectorModal?: ShipToSelectorModalStyles;
    billToSelectorModal?: BillToSelectorModalStyles;
    shipToGridItem?: GridItemProps;
    shippingAddressForm?: ShippingAddressFormStyles;
    addressInfoDisplay?: AddressInfoDisplayStyles;
    useShippingAddressGridItem?: GridItemProps;
    useShippingAddressCheckbox?: CheckboxPresentationProps;
    billToGridItem?: GridItemProps;
    billingAddressForm?: BillingAddressFormStyles;
    saveButtonGridItem?: GridItemProps;
    saveButton?: ButtonPresentationProps;
    linksWrapper?: InjectableCss;
    createNewAddressModal?: ModalPresentationProps;
    addCustomerAddressForm?: CustomerAddressFormStyles;
}

export const onePageCheckoutShippingSectionStyles: OnePageCheckoutShippingSectionStyles = {
    loadingWrapper: {
        css: css`
            display: flex;
            height: 120px;
            justify-content: center;
            align-items: center;
        `,
    },
    container: { gap: 10 },
    titleGridItem: { width: 10 },
    titleText: {
        variant: "h5",
        css: css`
            margin: 0;
        `,
    },
    titleEditGridItem: {
        width: 2,
        css: css`
            justify-content: end;
            align-items: center;
        `,
    },
    pickUpGridItem: {
        width: 12,
        css: css`
            padding-bottom: 24px;
        `,
    },
    headingGridItem: {
        width: 12,
        css: css`
            justify-content: space-between;
            padding: 15px 5px 5px 5px;
        `,
    },
    heading: { weight: 600 },
    errorMessage: { weight: 600, color: "red" },
    shipToGridItem: { width: 12 },
    useShippingAddressGridItem: {
        width: 12,
        css: css`
            padding: 20px 5px;
        `,
    },
    billToGridItem: { width: 12 },
    saveButtonGridItem: { width: 12 },
    saveButton: {
        css: css`
            margin-top: 10px;
        `,
    },
};

const styles = onePageCheckoutShippingSectionStyles;

const OnePageCheckoutShippingSection = ({
    isShippingAddressUpdateRequired,
    isBillingAddressUpdateRequired,
    isBillingAddressTheSameAsShipping,
    isSavingShipping,
    cart,
    shipToState,
    billToState,
    currentUserIsGuest,
    countries,
    shipTosDataView,
    shipToAddressFields,
    billToAddressFields,
    fulfillmentMethod,
    pickUpWarehouse,
    newAddress,
    theme,
    shipToText,
    loadShipTos,
    loadShipTo,
    loadBillTo,
    setIsBillingAddressTheSameAsShipping,
    setAddressBookOverlayIsOpen,
    setStorePickupOverlayIsOpen,
    saveShipping,
    setPaymentDetailsState,
    defaultCustomer,
    cartPageUrl,
    updateShipTo,
    history,
    toaster,
    isEmea,
    danaGeneralSettings
}: Props) => {
    const context = useContext(OnePageCheckoutFormContext);
    const [isEditing, setIsEditing] = useState(false);
    const [isShipToSelectorModalOpen, setIsShipToSelectorModalOpen] = useState(false);
    const [isBillToSelectorModalOpen, setIsBillToSelectorModalOpen] = useState(false);
    const [createnewAddress, setNewAddress] = useState<ShipToModel | undefined>(undefined);
    const [isShipToCreating, setIsShipToCreating] = useState(false);
    const [isOpen, setOpenModal] = useState(false);
    const [validAddress, setValidAddress] = useState<boolean>(true);
    const soldToObj: SoldToAddress | undefined = cart?.properties?.soldToAddress
        ? JSON.parse(cart.properties.soldToAddress)
        : undefined;

    useEffect(() => {
        if ((isShippingAddressUpdateRequired || isBillingAddressUpdateRequired) && shipToState.value) {
            setIsEditing(false);
        }
    }, [isShippingAddressUpdateRequired, isBillingAddressUpdateRequired, shipToState.value]);

    const shipTo = shipToState.value;
    const billTo = billToState.value;
    const firstSelectedCustomer = defaultCustomer.value;
    useEffect(() => {
        if (cart && shipTosDataView && !shipTosDataView.value && !shipTosDataView.isLoading) {
            loadShipTos({ billToId: cart.billToId, expand: ["validation"], exclude: ["showAll"] });
        }
    });

    useEffect(() => {
        if (shipTosDataView && !shipTosDataView.isLoading && shipTosDataView?.value) {
            const address = shipTosDataView?.value!.find(s => s.isNew && !s.oneTimeAddress);
            if (address) {
                setNewAddress(address);
            }
        }
    }, [shipTosDataView?.value]);

    useEffect(() => {
        if (!shipToState.value && !shipToState.isLoading && cart && cart.billToId && cart.shipToId) {
            loadShipTo({ billToId: cart.billToId, shipToId: cart.shipToId });
        }
    }, [shipToState]);

    useEffect(() => {
        if (!billToState.value && !billToState.isLoading && cart && cart.billToId) {
            loadBillTo({ billToId: cart.billToId });
        }
    }, [billToState]);

    if (!cart) {
        return (
            <StyledWrapper {...styles.loadingWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    if (!countries || !billTo || (!shipTo && !newAddress)) {
        return null;
    }

    const useShippingAddressChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setIsBillingAddressTheSameAsShipping({ isBillingAddressTheSameAsShipping: value });
    };

    const editShippingAddressClickHandler = () => {
        theme.addressBook?.useOverlay
            ? setAddressBookOverlayIsOpen({ isOpen: true, isBillTo: false })
            : setIsShipToSelectorModalOpen(true);
    };

    const shipToSelectorModalCloseHandler = () => {
        setIsShipToSelectorModalOpen(false);
    };

    const editBillingAddressClickHandler = () => {
        theme.addressBook?.useOverlay
            ? setAddressBookOverlayIsOpen({ isOpen: true, isBillTo: true })
            : setIsBillToSelectorModalOpen(true);
    };

    const billToSelectorModalCloseHandler = () => {
        setIsBillToSelectorModalOpen(false);
    };

    const editPickUpWarehouseClickHandler = () => {
        setStorePickupOverlayIsOpen({ isOpen: true });
    };

    const titleEditClickHandler = () => {
        if (currentUserIsGuest) {
            setIsEditing(true);
        } else if (fulfillmentMethod === FulfillmentMethod.PickUp) {
            setAddressBookOverlayIsOpen({ isOpen: true, isBillTo: true });
        }
    };

    const saveClickHandler = async () => {
        context.formSubmitAttempted = true;

        const validators: (() => Promise<boolean>)[] = [];
        Object.keys(context.validators).forEach(key => {
            const validator = context.validators[key];
            if (validator) {
                validators.push(validator);
            }
        });
        const results = await Promise.all(validators.map(v => v()));

        if (results.every(o => o)) {
            saveShipping({
                onSuccess: () => {
                    setIsEditing(false);
                    setPaymentDetailsState({ paymentDetailsState: "active" });
                },
            });
        } else {
            scrollToFirstFormError();
        }
    };

    const onModalClose = () => {
        setOpenModal(false);
    };

    const isIntegerString = (str: string): boolean => {
        return /^-?\d+$/.test(str.trim()) && Number.isInteger(Number(str));
    };

    const newShipToFormSubmitHandler = (_: React.FormEvent<HTMLFormElement>, address: ShipToModel) => {
        address.oneTimeAddress = true;
        if (danaGeneralSettings?.validateAddress) {
            danaValidateAddress(address).then(validateResult => {
                if (validateResult.results.length > 0 && validateResult.results[0].matchConfidence.score >= 0.89) {
                    const addressResult = validateResult.results[0];
                    const originalPostalCode = address.postalCode;
                    let originalStreetNumber = address.address1.split(" ").at(0);
                    if (!isIntegerString(originalStreetNumber ?? '')) {
                        originalStreetNumber = address.address1.split(" ").reverse().at(0);
                    }
                    address.address1 = `${addressResult.address.streetNumber ?? originalStreetNumber} ${addressResult.address.streetName}`;
                    address.city = addressResult.address.localName;
                    address.postalCode = addressResult.address.extendedPostalCode ?? addressResult.address.postalCode;
                    if (address.postalCode.includes(",")) {
                        address.postalCode =
                            address.postalCode
                                .split(",")
                                .find(postal => postal.trim() === originalPostalCode)
                                ?.trim() ?? address.postalCode;
                        if (address.postalCode.length > 10) {
                            address.postalCode = originalPostalCode;
                        }
                    }
                    updateShipTo({
                        billToId: cart?.billToId || "",
                        shipTo: address,
                        isEdit: true,
                        onSuccess: hasCartlines => {
                            if (!hasCartlines && cartPageUrl) {
                                history.push(cartPageUrl);
                            }
                        },
                    });

                    setIsShipToCreating(false);
                    onModalClose();
                } else {
                    setValidAddress(false);

                }
            });
        } else {
            updateShipTo({
                billToId: cart?.billToId || "",
                shipTo: address,
                isEdit: true,
                onSuccess: hasCartlines => {
                    if (!hasCartlines && cartPageUrl) {
                        history.push(cartPageUrl);
                    }
                },
            });

            setIsShipToCreating(false);
            onModalClose();
        }

    };

    const createNewAddressCancelHandler = () => {
        setIsShipToCreating(false);
        setOpenModal(false);
    };

    const createNewAddressHandler = () => {
        const address =
            shipTosDataView && !shipTosDataView.isLoading
                ? shipTosDataView?.value!.find(s => s.isNew && !s.oneTimeAddress)
                : undefined;
        if (address) {
            setNewAddress(address);
        }
        setOpenModal(true);
        setIsShipToCreating(true);
    };

    return (
        <LoadingOverlay loading={isSavingShipping}>
            <GridContainer {...styles.container} data-test-selector="shippingAndBillingSection">
                {fulfillmentMethod === FulfillmentMethod.PickUp && pickUpWarehouse && (
                    <>
                        <GridItem {...styles.titleGridItem}>
                            <Typography as="h2" {...styles.titleText}>
                                {translate("Pick Up Address")}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.titleEditGridItem}>
                            <Link {...styles.titleEditLink} onClick={editPickUpWarehouseClickHandler}>
                                {translate("Edit")}
                            </Link>
                        </GridItem>
                        <GridItem {...styles.pickUpGridItem}>
                            <AddressInfoDisplay
                                {...pickUpWarehouse}
                                companyName={pickUpWarehouse.description || pickUpWarehouse.name}
                                extendedStyles={styles.addressInfoDisplay}
                            />
                        </GridItem>
                    </>
                )}
                <GridItem {...styles.titleGridItem}>
                    <Typography as="h2" {...styles.titleText}>
                        {fulfillmentMethod === FulfillmentMethod.Ship
                            ? translate("Address")
                            : translate("Billing Address")}
                    </Typography>
                </GridItem>
                {isEditing && (
                    <>
                        {fulfillmentMethod === FulfillmentMethod.Ship && shipToAddressFields && (
                            <GridItem {...styles.shipToGridItem}>
                                <ShippingAddressForm
                                    countries={countries}
                                    fieldDisplay={shipToAddressFields}
                                    extendedStyles={styles.shippingAddressForm}
                                />
                            </GridItem>
                        )}
                        {fulfillmentMethod === FulfillmentMethod.Ship && (
                            <GridItem {...styles.useShippingAddressGridItem}>
                                <Checkbox
                                    {...styles.useShippingAddressCheckbox}
                                    onChange={useShippingAddressChangeHandler}
                                    checked={isBillingAddressTheSameAsShipping}
                                    data-test-selector="useShippingAddressCheckbox"
                                >
                                    {translate("Billing address is the same as shipping")}
                                </Checkbox>
                            </GridItem>
                        )}
                        {billToAddressFields &&
                            (!isBillingAddressTheSameAsShipping || fulfillmentMethod === FulfillmentMethod.PickUp) && (
                                <GridItem {...styles.billToGridItem}>
                                    <BillingAddressForm
                                        countries={countries}
                                        fieldDisplay={billToAddressFields}
                                        extendedStyles={styles.billingAddressForm}
                                    />
                                </GridItem>
                            )}
                        <GridItem {...styles.saveButtonGridItem}>
                            <Button
                                {...styles.saveButton}
                                onClick={saveClickHandler}
                                data-test-selector="saveAndContinueButton"
                            >
                                {translate("Save & Continue")}
                            </Button>
                        </GridItem>
                    </>
                )}
                {!isEditing && (
                    <StyledWrapper className="checkout-address-container">
                        {billTo && soldToObj?.Id === billTo.id && (
                            <StyledWrapper className="checkout-address-wrapper">
                                {fulfillmentMethod === FulfillmentMethod.Ship && (
                                    <GridItem {...styles.headingGridItem}>
                                        <Typography {...styles.heading}>
                                            {translate("Sold-to")}:{billTo?.customerSequence}
                                        </Typography>
                                    </GridItem>
                                )}
                                <GridItem {...styles.billToGridItem} data-test-selector="BillingAddress">
                                    <AddressInfoDisplay
                                        address1={billTo.address1}
                                        address2={billTo.address2}
                                        city={billTo.city}
                                        postalCode={billTo?.postalCode || ""}
                                        firstName={billTo?.firstName || ""}
                                        lastName={billTo?.lastName || ""}
                                        companyName={billTo?.companyName || ""}
                                        state={billTo.state ? billTo.state.abbreviation : undefined}
                                        country={billTo.country ? billTo.country.abbreviation : undefined}
                                        extendedStyles={styles.addressInfoDisplay}
                                    />
                                </GridItem>
                            </StyledWrapper>
                        )}
                        {billTo && soldToObj?.Id !== billTo.id && (
                            <StyledWrapper className="checkout-address-wrapper">
                                <GridItem {...styles.headingGridItem}>
                                    <Typography {...styles.heading}>
                                        {translate("Sold-to")}:{soldToObj?.CustomerSequence}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.billToGridItem} data-test-selector="BillingAddress">
                                    <AddressInfoDisplay
                                        address1={soldToObj?.Address1 || ""}
                                        address2={soldToObj?.Address2 || ""}
                                        city={soldToObj?.City || ""}
                                        postalCode={soldToObj?.PostalCode || ""}
                                        firstName={soldToObj?.FirstName || ""}
                                        lastName={soldToObj?.LastName || ""}
                                        companyName={soldToObj?.CompanyName || ""}
                                        state={soldToObj?.State}
                                        country={soldToObj?.Country}
                                        extendedStyles={styles.addressInfoDisplay}
                                    />
                                </GridItem>
                            </StyledWrapper>
                        )}
                        {(currentUserIsGuest || fulfillmentMethod === FulfillmentMethod.PickUp) && (
                            <GridItem {...styles.titleEditGridItem}>
                                <Link
                                    {...styles.titleEditLink}
                                    onClick={titleEditClickHandler}
                                    data-test-selector="editPickupShippingAddressButton"
                                >
                                    {translate("Edit")}
                                </Link>
                            </GridItem>
                        )}
                        {fulfillmentMethod === FulfillmentMethod.Ship && (shipTo || newAddress) && (
                            <StyledWrapper className="checkout-address-wrapper">
                                <GridItem {...styles.headingGridItem}>
                                    <Typography {...styles.heading}>
                                        {translate(shipToText)}:{shipTo?.customerSequence}
                                    </Typography>
                                    {!currentUserIsGuest && (
                                        <>
                                            {firstSelectedCustomer?.properties?.isBillTo?.toLowerCase() === "false" &&
                                                firstSelectedCustomer?.properties?.isSoldTo?.toLowerCase() === "false" &&
                                                firstSelectedCustomer?.properties?.isShipTo?.toLowerCase() ===
                                                "true" ? null : (
                                                <>
                                                    {shipTo?.lastName === "02" || soldToObj?.LastName === "02" ? (
                                                        <Link
                                                            {...styles.editLink}
                                                            onClick={editShippingAddressClickHandler}
                                                            data-test-selector="editShippingAddressButton"
                                                        >
                                                            {translate("Change")}
                                                            <VisuallyHidden>
                                                                {translate(shipToText)}:{shipTo?.customerSequence}
                                                            </VisuallyHidden>
                                                        </Link>
                                                    ) : (
                                                        <StyledWrapper {...styles.linksWrapper}>
                                                            <Link
                                                                {...styles.editLink}
                                                                onClick={editShippingAddressClickHandler}
                                                                data-test-selector="editShippingAddressButton"
                                                            >
                                                                {translate("Change")}
                                                                <VisuallyHidden>
                                                                    {translate(shipToText)}:{shipTo?.customerSequence}
                                                                </VisuallyHidden>
                                                            </Link>
                                                            {isEmea === false && (
                                                                <Link
                                                                    {...styles.editLink}
                                                                    onClick={createNewAddressHandler}
                                                                    data-test-selector="editShippingAddressButton"
                                                                >
                                                                    {translate("Add New")}
                                                                    <VisuallyHidden>
                                                                        {translate(shipToText)}:
                                                                        {shipTo?.customerSequence}
                                                                    </VisuallyHidden>
                                                                </Link>
                                                            )}
                                                        </StyledWrapper>
                                                    )}
                                                </>
                                            )}

                                            <ShipToSelectorModal
                                                isOpen={isShipToSelectorModalOpen}
                                                onModalClose={shipToSelectorModalCloseHandler}
                                                extendedStyles={styles.shipToSelectorModal}
                                            />
                                        </>
                                    )}
                                </GridItem>
                                <GridItem {...styles.shipToGridItem} data-test-selector="ShippingAddress">
                                    {shipTo && (
                                        <AddressInfoDisplay
                                            address1={shipTo.address1}
                                            address2={shipTo.address2}
                                            city={shipTo.city}
                                            postalCode={shipTo?.postalCode || ""}
                                            firstName={shipTo?.firstName || ""}
                                            lastName={shipTo?.lastName || ""}
                                            companyName={shipTo?.companyName || ""}
                                            state={shipTo.state ? shipTo.state.abbreviation : undefined}
                                            country={shipTo.country ? shipTo.country.abbreviation : undefined}
                                            extendedStyles={styles.addressInfoDisplay}
                                        />
                                    )}
                                </GridItem>
                            </StyledWrapper>
                        )}
                        {billTo && (
                            <StyledWrapper className="checkout-address-wrapper">
                                {fulfillmentMethod === FulfillmentMethod.Ship && (
                                    <GridItem {...styles.headingGridItem}>
                                        <Typography {...styles.heading}>
                                            {translate("Bill-to")} ({translate("Invoice to be sent to")}):
                                            {billTo.customerSequence}
                                        </Typography>
                                    </GridItem>
                                )}
                                <GridItem {...styles.billToGridItem} data-test-selector="BillingAddress">
                                    <AddressInfoDisplay
                                        address1={billTo.address1}
                                        address2={billTo.address2}
                                        city={billTo.city}
                                        postalCode={billTo?.postalCode || ""}
                                        firstName={billTo?.firstName || ""}
                                        lastName={billTo?.lastName || ""}
                                        companyName={billTo?.companyName || ""}
                                        state={billTo.state ? billTo.state.abbreviation : undefined}
                                        country={billTo.country ? billTo.country.abbreviation : undefined}
                                        extendedStyles={styles.addressInfoDisplay}
                                    />
                                </GridItem>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                )}
                <Modal
                    {...styles.createNewAddressModal}
                    headline={translate("Create New Address")}
                    isOpen={isOpen}
                    handleClose={onModalClose}
                    data-test-selector="onePageCheckoutShipping_addressModal"
                >
                    {createnewAddress && countries && shipToAddressFields ? (
                        <>
                            {!validAddress ? (
                                <Typography {...styles.errorMessage}>
                                    {translate("Invalid address. Please make sure the address is correct and includes a street number.")}
                                </Typography>
                            ) : (
                                <></>
                            )}
                            <CustomerAddressForm
                                address={createnewAddress}
                                countries={countries}
                                addressFieldDisplayCollection={shipToAddressFields}
                                onSubmit={newShipToFormSubmitHandler}
                                onCancel={createNewAddressCancelHandler}
                                extendedStyles={styles.addCustomerAddressForm}
                            />
                        </>
                    ) : (
                        <LoadingSpinner></LoadingSpinner>
                    )}
                </Modal>
            </GridContainer>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(OnePageCheckoutShippingSection)),
    definition: {
        displayName: "Shipping Section",
        group: "One Page Checkout",
        allowedContexts: ["OnePageCheckoutPage"],
    },
};

export default widgetModule;
